import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpUrlEncodingCodec } from '@angular/common/http';
import { ClaimsService } from '../../core/services/claims.service';
import { DocumentViewerComponent } from '../../shared/components/document-viewer/document-viewer.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-claim-letter',
  templateUrl: './claim-letter.component.html',
  styleUrls: ['./claim-letter.component.scss'],
  providers: [HttpUrlEncodingCodec]
})
export class ClaimLetterComponent implements OnInit {
  token: string | null = null;
  claimData: any = null;
  isLoading: boolean = false;
  sidenavToggle: boolean = false;
  errorMessage: string | null = null;
  displayedColumns: string[] = ['documentType', 'name', 'actions'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private claimsService: ClaimsService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private httpUrlEncodingCodec: HttpUrlEncodingCodec
  ) { }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token');
    console.log('Token in ClaimLetterComponent:', this.token);

    let sideToken = localStorage.getItem('sidenav');
    if (sideToken === 'true') {
      this.toggleNav(sideToken);
    }

    if (this.token) {
      this.fetchClaimLetterData();
    } else {
      this.errorMessage = 'No token provided in URL. Please make sure the link is complete.';
    }
  }

  fetchClaimLetterData() {
    this.isLoading = true;
    this.errorMessage = null;
    this.claimsService.getClaimLetterData(this.token!).subscribe(
      (res: any) => {
        this.isLoading = false;
        this.claimData = res.body ? res.body : res;
        if (!this.claimData || (typeof this.claimData === 'object' && Object.keys(this.claimData).length === 0)) {
          Swal.fire('', 'Details not available at the moment, please try again after some time', 'info').then(() => {
            this.router.navigate(['/home']);
          });
        }
      },
      (err: any) => {
        this.isLoading = false;
        this.errorMessage = err.error ? (err.error.message || err.error) : err.message;
        Swal.fire('', this.errorMessage || 'An error occurred', 'error').then(() => {
          this.router.navigate(['/home']);
        });
      }
    );
  }

  copyClaimId() {
    if (this.claimData && this.claimData.alternateClaimId) {
      navigator.clipboard.writeText(this.claimData.alternateClaimId).then(
        () => {
          this.snackBar.open('Claim ID copied to clipboard', 'Dismiss', {
            duration: 3000,
            panelClass: ['snackbar-success']
          });
        },
        () => {
          this.snackBar.open('Failed to copy Claim ID', 'Dismiss', {
            duration: 3000
          });
        }
      );
    }
  }

  viewClaim() {
    if (!this.claimData) {
      return;
    }
    const claimId = this.claimData.claimId;
    if (!claimId) {
      Swal.fire('', 'No Claim ID found to view details', 'warning');
      return;
    }
    const tempClaimId = claimId.split('/').join('_');
    
    let routePath = '/claims/claimDetails/';
    if (this.claimData.claimType === 'RETAIL') {
      routePath = '/claims/retailClaimDetails/';
      this.router.navigate([routePath + tempClaimId + '/view'], { skipLocationChange: true });
    } else if (this.claimData.claimStatus === 'PRE_AUTH_PENDING_FROM_HOSPITAL') {
      routePath = '/claims/editableClaimDetails/';
      this.router.navigate([routePath + tempClaimId + '/view'], { skipLocationChange: true });
    } else if (this.claimData.claimType || this.claimData.claimStatus) {
      this.router.navigate([routePath + tempClaimId + '/view'], { skipLocationChange: true });
    } else {
      Swal.showLoading();
      this.claimsService.getClaimDetails(claimId).subscribe(
        (res: any) => {
          Swal.close();
          const details = res.body ? res.body : res;
          if (details) {
            if (details.claimType === 'RETAIL') {
              routePath = '/claims/retailClaimDetails/';
            } else if (details.claimStatus === 'PRE_AUTH_PENDING_FROM_HOSPITAL') {
              routePath = '/claims/editableClaimDetails/';
            }
          }
          this.router.navigate([routePath + tempClaimId + '/view'], { skipLocationChange: true });
        },
        (err: any) => {
          Swal.close();
          this.router.navigate([routePath + tempClaimId + '/view'], { skipLocationChange: true });
        }
      );
    }
  }

  viewDocument(doc: any) {
    if (!doc.url) {
      Swal.fire('', 'No document URL found', 'warning');
      return;
    }
    Swal.showLoading();
    const decodedUrl = this.httpUrlEncodingCodec.decodeValue(doc.url);
    this.claimsService.fetchDocument(decodedUrl).subscribe(
      (res: any) => {
        Swal.close();
        if (res.body.docType === 'pdf') {
          this.dialog.open(DocumentViewerComponent, {
            width: '80vw',
            data: { imageSource: `${res.body.docBaseSF}` }
          });
        } else {
          // Trigger a base64 download for other file types
          const downloadLink = document.createElement('a');
          const fileExtension = res.body.docType || 'png';
          downloadLink.href = `data:application/octet-stream;base64,${res.body.docBaseSF}`;
          downloadLink.download = doc.name || res.body.docName || `document.${fileExtension}`;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      },
      (err: any) => {
        Swal.close();
        Swal.fire('', err.error ? err.error : err.message, 'error');
      }
    );
  }

  toggleNav(value: any) {
    if (value === 'true' || value === true) {
      this.sidenavToggle = true;
    } else if (value === 'false' || value === false) {
      this.sidenavToggle = false;
    }
  }
}

